import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiChevronDown, FiChevronRight, FiUser, FiUsers, FiInfo, FiAward, FiCalendar, FiMapPin, FiPhone, FiMail } from "react-icons/fi";

const MLMTreeModal = ({ isOpen, onClose, partner }) => {
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  
  if (!isOpen || !partner) return null;
  
  const { mlmTree } = partner;
  
  if (!mlmTree) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    MLM Network
                  </h2>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <FiX size={24} />
                  </button>
                </div>
                
                <div className="text-center py-8">
                  <div className="text-gray-500 dark:text-gray-400 text-lg">
                    No MLM data available for this partner
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
  
  const { parent, ancestors, children, depth } = mlmTree;
  
  // Toggle node expansion
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };
  
  // Handle member selection
  const handleMemberClick = (member, e) => {
    e.stopPropagation();
    setSelectedMember(member);
  };
  
  // Close member details
  const closeMemberDetails = () => {
    setSelectedMember(null);
  };
  
  // Render member details
  const renderMemberDetails = (member) => {
    if (!member) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white mr-3">
              <FiUser size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">{member.fullName}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">ID: {member.sponsorCode}</p>
            </div>
          </div>
          <button 
            onClick={closeMemberDetails}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-start">
              <FiAward className="text-amber-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Rank</p>
                <p className="text-gray-800 dark:text-white">{member.rank || 'No rank'}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <FiCalendar className="text-blue-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Join Date</p>
                <p className="text-gray-800 dark:text-white">
                  {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <FiMapPin className="text-green-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</p>
                <p className="text-gray-800 dark:text-white">
                  {member.city && member.state ? `${member.city}, ${member.state}` : 'Not specified'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start">
              <FiPhone className="text-purple-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                <p className="text-gray-800 dark:text-white">{member.phone || 'Not provided'}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <FiMail className="text-red-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                <p className="text-gray-800 dark:text-white">{member.email || 'Not provided'}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <FiUsers className="text-indigo-500 mt-1 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Network Position</p>
                <p className="text-gray-800 dark:text-white">
                  {member === partner ? 'Current Partner' : 
                   parent && member._id === parent._id ? 'Direct Parent' :
                   ancestors && ancestors.some(a => a._id === member._id) ? 'Ancestor' :
                   children && children.some(c => c._id === member._id) ? 'Direct Child' : 'Related Member'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {member.mlmTree && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              <FiInfo className="mr-1" />
              MLM Network Stats
            </h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Depth</p>
                <p className="text-sm font-medium text-gray-800 dark:text-white">{member.mlmTree.depth || 0}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Children</p>
                <p className="text-sm font-medium text-gray-800 dark:text-white">{member.mlmTree.children?.length || 0}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded p-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">Ancestors</p>
                <p className="text-sm font-medium text-gray-800 dark:text-white">{member.mlmTree.ancestors?.length || 0}</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  };
  
  // Render a member node
  const renderMemberNode = (member, type, isExpanded) => {
    if (!member) return null;
    
    const nodeId = member._id || member.sponsorCode;
    const hasChildren = type === 'partner' && children && children.length > 0;
    
    return (
      <div className="mb-4">
        <div 
          className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
            type === 'partner' 
              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/40 dark:hover:to-indigo-800/40' 
              : type === 'parent'
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30'
                : type === 'ancestor'
                  ? 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30'
                  : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30'
          }`}
          onClick={(e) => {
            if (hasChildren) {
              toggleNode(nodeId);
            }
            handleMemberClick(member, e);
          }}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white mr-3 ${
            type === 'partner' 
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
              : type === 'parent'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                : type === 'ancestor'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500'
          }`}>
            {type === 'partner' ? <FiUser size={20} /> : <FiUsers size={20} />}
          </div>
          
          <div className="flex-1">
            <div className="font-medium text-gray-800 dark:text-white">{member.fullName}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {member.sponsorCode} • {member.rank || 'No rank'}
            </div>
          </div>
          
          {hasChildren && (
            <div className="ml-2">
              {isExpanded ? <FiChevronDown size={20} /> : <FiChevronRight size={20} />}
            </div>
          )}
        </div>
        
        {isExpanded && hasChildren && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="pl-6 mt-2 border-l-2 border-gray-200 dark:border-gray-700"
          >
            {children.map((child, index) => (
              <div key={child._id || index} className="mb-3">
                {renderMemberNode(child, 'child', expandedNodes[child._id])}
              </div>
            ))}
          </motion.div>
        )}
      </div>
    );
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                  MLM Network
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center text-white mr-4">
                    <FiUser size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 dark:text-white text-lg">{partner.fullName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {partner.sponsorCode} • {partner.rank || 'No rank'} • Depth: {depth}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 overflow-y-auto max-h-[60vh] pr-2">
                  {/* Parent */}
                  {parent && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-green-700 dark:text-green-400 mb-3 flex items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                        Parent
                      </h3>
                      {renderMemberNode(parent, 'parent', false)}
                    </div>
                  )}
                  
                  {/* Ancestors */}
                  {ancestors && ancestors.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-purple-700 dark:text-purple-400 mb-3 flex items-center">
                        <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                        Ancestors ({ancestors.length})
                      </h3>
                      <div className="space-y-3">
                        {ancestors.map((ancestor, index) => (
                          <div key={ancestor._id || index}>
                            {renderMemberNode(ancestor, 'ancestor', false)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Children */}
                  {children && children.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-amber-700 dark:text-amber-400 mb-3 flex items-center">
                        <div className="w-2 h-2 rounded-full bg-amber-500 mr-2"></div>
                        Children ({children.length})
                      </h3>
                      <div className="space-y-3">
                        {children.map((child, index) => (
                          <div key={child._id || index}>
                            {renderMemberNode(child, 'child', expandedNodes[child._id])}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!parent && (!ancestors || ancestors.length === 0) && (!children || children.length === 0) && (
                    <div className="text-center py-8">
                      <div className="text-gray-500 dark:text-gray-400">
                        This partner has no connections in the MLM network yet.
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="lg:col-span-1">
                  <div className="sticky top-0">
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                      Member Details
                    </h3>
                    {selectedMember ? (
                      renderMemberDetails(selectedMember)
                    ) : (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                        <p className="text-gray-500 dark:text-gray-400">
                          Click on a member to view their details
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MLMTreeModal; 